import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams} from "react-router";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {FaPlus} from "react-icons/fa";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import materialSupplierService from "../../../../../services/web/material-supplier-service.js";
import {toast} from "react-toastify";
import InputForm from "../../../../../components/input/input-form.jsx";
import {getCities, getNeighborhoods, getStreets} from "../../../../../utils/location-utils.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import OptionsUtils from "../../../../../utils/options-utils.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import AlertMessages from "../../../../../components/messages/alert-message.js";
import Swal from "sweetalert2";
import {MaskDefault} from "../../../../../utils/mask.js";

const initialData = {
    name: "",
    social_name: "",
    cnpj: "",
    phone: "",
    phone_type: "",
    email: "",
    address: "",
    number: "",
    neighborhood: "",
    complement: "",
    cep: "",
    city: ""
}

const typePhones = [
    {value: "CELULAR", label: "CELULAR"},
    {value: "TELEFONE", label: "TELEFONE"},
]

const MaterialSuppliersForm = () => {
    const navigate = navigateHook();
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(initialData)
    const saveButtonRef = useRef(null)

    const onChange = ({target: {value, id}}) => setData(prev => ({...prev, [id]: id === "cnpj" ? value?.toUpperCase() : value}))
    const onChangeSelection = (id, value) => setData(prev => ({...prev, [id]: value || ""}));

    const {data: supplier, refetch, fetchStatus} = useQuery({
        queryFn: () => materialSupplierService.getById(id),
        queryKey: ['request', {id}],
        enabled: !!id,
    });

    useEffect(() => {
        if (fetchStatus === "idle") {
            const supplierNormalized = projectUtils.normalizeData(supplier)
            setData(supplierNormalized)
        }
    }, [fetchStatus, supplier]);

    const save = () => {
        const formData = new FormData();

        const form = {
            name: data.name ?? "",
            social_name: data.social_name ?? "",
            phone_type: data.phone_type ?? "",
            email: data.email ?? "",
            address: data.address ?? "",
            number: data.number ?? "",
            neighborhood: data.neighborhood ?? "",
            complement: data.complement ?? "",
            city: data.city ?? "",
            cnpj: data.cnpj ? data.cnpj.replace(/[^a-zA-Z0-9]/g, '') : "",
            cep: data.cep ? data.cep.replace(/\D/g, '') : "",
            phone: data.phone ? data.phone.replace(/\D/g, '') : ""
        };

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        if (id) formData.append('_method', 'PUT');

        setIsLoading(true);

        materialSupplierService.save(formData, id)
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/materials/suppliers/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? "Não foi possível salvar.";

                projectUtils.swalErrorMessage('', errMessage, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const remove = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o fornecedor "${data.name}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return
            materialSupplierService.delete(id)
                .then(() => {
                    projectUtils.success(true, "Excluído com sucesso")
                    navigate("/materials/suppliers")
                })
                .catch(error => {
                    console.error(error);
                    let message = 'Não foi possível excluir.';
                    if (error.response.status !== 500) {
                        message = error.response.data.message;
                    }

                    projectUtils.swalErrorMessage('', message, 'Ok');
                });
        });
    }

    const openForm = () => {
        setData(initialData)
        const urlDev = `/materials/suppliers/form`;
        navigate(urlDev)
    }

    useGlobalKey("Enter", () => saveButtonRef.current?.click())
    return (<Template isLoading={isLoading}>
        <Template.Header>
            <Template.Title>
                Fornecedor
            </Template.Title>
            {
                id &&
                <Button target={"primary-outline"} onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar fornecedor</span>
                </Button>
            }
        </Template.Header>
        <Template.Body>
            <Card>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-4">
                            <InputForm id="name" value={data.name} label="Nome fantasia *" onChange={onChange}/>
                        </div>
                        <div className="col-span-4">
                            <InputForm id="social_name" value={data.social_name} label="Razão social *" onChange={onChange}/>
                        </div>
                        <div className="col-span-4">
                            <InputForm
                                id="cnpj"
                                value={data.cnpj}
                                label="CNPJ *"
                                onChange={onChange}
                                mask={MaskDefault.cnpj}/>
                        </div>

                        <div className="col-span-4">
                            <SelectComponent
                                loadOptions={getStreets}
                                value={OptionsUtils.getValue(data.address)}
                                isAsync={true}
                                label={"Logradouro"}
                                onChange={(selected) => onChangeSelection("address", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                loadOptions={getNeighborhoods}
                                value={OptionsUtils.getValue(data.neighborhood)}
                                isAsync={true}
                                label={"Bairro"}
                                onChange={(selected) => onChangeSelection("neighborhood", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className={"col-span-4"}>
                            <InputForm id="number" value={data.number} label="Número"
                                      onChange={onChange}/>
                        </div>

                        <div className="col-span-4">
                            <InputForm
                                id="cep"
                                value={data.cep}
                                label="CEP"
                                onChange={onChange}
                                mask={[{ mask: "00.000-000" }]}/>
                        </div>
                        <div className="col-span-4">
                            <SelectComponent
                                loadOptions={getCities}
                                value={optionsUtils.getValue(data.city)}
                                isAsync={true}
                                label={"Município"}
                                onChange={(selected) => onChangeSelection("city", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-4">
                            <InputForm id="complement" value={data.complement} label="Complemento" onChange={onChange}/>
                        </div>

                        <div className="col-span-4">
                            <InputForm
                                id="phone"
                                value={data.phone}
                                label="Número do contato"
                                onChange={onChange}
                                onBlur={onChange}
                                mask={[{ mask: "(00) 0000-0000" }, { mask: "(00) 00000-0000" }]}/>
                        </div>
                        <div className="col-span-8">
                            <InputForm value={data.email} label="E-mail" id={"email"} onChange={onChange}/>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className={"mt-2"}>
                <Card.Body>
                    <div className="w-full flex justify-end">
                        <div className="flex gap-2">
                            {
                                (id) &&
                                <>
                                    <Button target="danger" onClick={remove} actionFeedbackType='delete'>

                                        Excluir
                                    </Button>
                                </>
                            }

                            <Button reference={saveButtonRef} onClick={save} actionFeedbackType='save'>

                                Salvar
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Template.Body>
    </Template>);
};

export default MaterialSuppliersForm;